<template>
  <div
    v-if="categoryIcons?.length && parseInt(attributes?.colCount, 10) >= 4"
    :class="['category-icons-cont', globalClassAttributes]"
  >
    <div
      v-if="attributes?.heading"
      class="category-heading h2"
      v-html="sanitize(attributes?.heading, cleanStyles)"
    />
    <div :class="['grid-x grid-margin-x grid-margin-y', getGridClasses]">
      <template
        v-for="(icon, index) in categoryIcons"
        :key="index"
      >
        <a
          :ref="(el) => (iconRefs[getImageIcon(icon)?.id] = el)"
          :href="getUrl(icon)"
          :class="[
            'cell category-icon menu-link-med',
            { 'icon-border': hasBorder },
            { 'selected': areVisualFacets && identifySelectedBasedOnFacets(icon)}
          ]"
          @click="onClick(getImageIcon(icon), $event, icon)"
        >
          <div :style="`padding: ${iconPadding}`">
            <div :style="styleAttributes">
              <div
                :class="[
                  'icon-image-container',
                  { 'max-width': hasMaxWidth },
                  { 'icon-scroll': hasScroll },
                ]"
              >
                <ImageMap
                  :image-map="getImageIcon(icon)"
                  :show-wrapper-url="false"
                />
              </div>
            </div>
            <div
              v-if="hasChildrenLinks(icon) || icon?.adLink"
              class="category-text-wrapper"
              :style="`height:${textContainerHeight}`"
            >
              <span v-html="sanitize(getLabel(icon), cleanStyles)" />
            </div>
          </div>
        </a>
      </template>
    </div>
  </div>
</template>

<script setup>
import {
  computed, ref,
} from 'vue';
import { sanitize } from '@vue-util/sanitize';
import { fireTag } from '@common-vue/util/src/modules/tagManager';
import {
  getClassAttributes,
  getStyleAttributes,
} from '../../helpers/getAttributes';
import ImageMap from '../imageMap/ImageMap';
import cleanStyles from '../../helpers/getCleanStyles';

const props = defineProps({
  attributes: {
    type: Object,
    default: () => {},
  },
  categoryIcons: {
    type: Array,
    default: () => [],
  },
  hasMaxWidth: {
    type: Boolean,
    default: false,
  },
  iconPadding: {
    type: String,
    default: '16px',
  },
  textContainerHeight: {
    type: String,
    default: 'auto',
  },
  hasScroll: {
    type: Boolean,
    default: false,
  },
  hasBorder: {
    type: Boolean,
    default: false,
  },
  selectedByFacets: {
    type: Array,
    default: () => [],
  },
  areVisualFacets: {
    type: Boolean,
    default: false,
  },
  fireTagOnClick: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['selectedIcon']);
const styleAttributes = getStyleAttributes(props.attributes);

const globalClassAttributes = getClassAttributes(props.attributes);

const getGridClasses = computed(() => {
  const colCount = props.attributes?.colCount;
  const lgSize = parseInt(colCount, 10) <= 5 ? colCount : '6';
  return `${
    props.hasScroll ? `scroll-cont small-up-${colCount}` : 'small-up-2'
  } medium-up-4 large-up-${lgSize}`;
});

const identifySelectedBasedOnFacets = (icon) => props?.selectedByFacets.some((vf) => vf?.imageMap?.id === icon?.imageMap?.id);

const getImageIcon = (icon) => {
  if (icon?.imageMap) {
    return icon.imageMap;
  }
  if (icon?.image) {
    return icon.image;
  }
  if (icon?.banner) {
    return icon.banner;
  }

  return icon?.logo;
};

const hasChildrenLinks = (icon) => getImageIcon(icon)?.children?.length;

const getLabel = (icon) => (hasChildrenLinks(icon) ? getImageIcon(icon)?.children?.[0]?.name : (icon.adLink?.name || ''));

const getUrl = (icon) => {
  if (hasChildrenLinks(icon)) {
    return getImageIcon(icon)?.children?.[0]?.url;
  }
  if (icon.adLink) {
    return icon?.adLink?.url;
  }
  return icon?.url || '';
};

const iconRefs = ref({});

const fireLinkTag = (icon, entireIconObj) => {
  const iconChildObj = icon?.children?.[0] || entireIconObj?.adLink;
  const assetAnalytics = iconChildObj?.assetAnalytics || entireIconObj?.image?.assetAnalytics;
  const htmlString = iconChildObj?.name;
  const filterObj = htmlString?.match(/(<(p|div)[^>]*>)*(?<facet>.*?)<\/(p|div)>/);
  const filterName = filterObj?.groups?.facet || htmlString;
  const data = {
    link_name: `feature facet filter = ${filterName}`,
  };
  if (assetAnalytics) {
    data.event_name = 'content link click';
    data.asset_analytics = assetAnalytics;
  }
  fireTag('link', data);
};

const onClick = (icon, event, entireIconObj) => {
  fireLinkTag(icon, entireIconObj);
  let isSelected = false;
  if (props.hasBorder && icon?.id) {
    event.preventDefault();
    if (!iconRefs.value?.[icon?.id]?.classList?.contains('selected')) {
      iconRefs.value[icon.id].classList.add('selected');
      isSelected = true;
      if (props.fireTagOnClick) {
        const vfIconCategory = new URLSearchParams(icon?.children[0]?.url?.split('?')?.at(-1))?.get('id');
        const categoryId = new URLSearchParams(window?.location?.search)?.get('id');
        if (vfIconCategory === categoryId) {
          fireTag('link', {
            link_name: 'visual_filter_selected',
          });
        }
      }
    } else {
      iconRefs.value?.[icon?.id]?.classList.remove('selected');
    }
    emit('selectedIcon', { icon, isSelected });
  }
};
</script>

<style lang="scss" scoped>
@import "~@core/foundation/scss/foundation-6.7.4/scss/prototype/_spacing.scss";
.category-heading {
  @include margin-direction("bottom", map-get($spacer, "m"));
}

.category-text-wrapper {
  margin-top: rem-calc(16);
  text-align: center;

  &:hover {
    opacity: 0.65;
  }
}

.category-icon {
  background-color: $background-color;
}

.icon-image-container {
  margin: 0 auto;
  &.max-width {
    max-width: rem-calc(128);
    @include breakpoint(medium) {
      max-width: rem-calc(138);
    }
    @include breakpoint(large) {
      max-width: rem-calc(162);
    }
    &.icon-scroll {
      @include breakpoint(small only) {
        max-width: rem-calc(98);
      }
    }
  }
}

.grid-x {
  &.scroll-cont {
    flex-flow: row nowrap;
    overflow-x: auto;
    padding-bottom: rem-calc(32);
    @include breakpoint(large) {
      flex-flow: row wrap;
      overflow-x: hidden;
      padding-bottom: rem-calc(0);
    }
    .cell {
      @include breakpoint(small only) {
        width: 100%;
        max-width: 34%;
      }
    }
    &::-webkit-scrollbar {
      height: rem-calc(3);
      margin-top: 32px;
    }
    &::-webkit-scrollbar-track {
      background-color: $hover-color;
    }
    &::-webkit-scrollbar-thumb {
      height: rem-calc(3);
      background-color: $gray-1-color;
    }
  }
}
.icon-border {
  border: 2px solid transparent;

  &.selected {
    border-color: $black;
  }
}

</style>
<style lang="scss" brand="mcom" scoped>
@import "./../../scss/globalAttributes.mcom.scss";

.large-up-5 {
  .icon-image-container {
    &.max-width {
      @include breakpoint(large) {
        max-width: rem-calc(153.75);
      }
    }
  }
}
.icon-border {
  border-radius: 5px;
}
</style>

<style lang="scss" brand="bcom" scoped>
@import "./../../scss/globalAttributes.bcom.scss";

.large-up-5 {
  .icon-image-container {
    &.max-width {
      &.icon-scroll {
        @include breakpoint(large) {
          max-width: rem-calc(153.75);
        }
      }
    }
  }
}
</style>
